import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * The Loops this browser has looped into.
 *
 * Client-side only, on purpose. A Loop has no `RoomMember` row, so it never
 * appears in the room list the way a Knot does, and there is no "my loops"
 * endpoint to ask — this is the only thing the discovery feed's "Looped in"
 * filter can be driven by.
 *
 * It is therefore an approximation, and a weaker one on web than on a phone: a
 * Loop-in from another browser, or before the user cleared site data, is absent
 * here. That is why the same setter is also fed the authoritative `isLoopedIn`
 * from `/room/info` — the set converges on every Loop the user actually opens.
 *
 * Stored as an array rather than a Set because it is JSON-persisted.
 *
 * `getOnInit` is required: without it the atom reports its default until
 * something subscribes, so a freshly loaded page would render the "Looped in"
 * filter as empty and only fill it in later.
 */
export const loopedInIdsAtom = atomWithStorage<string[]>('loopedIn', [], undefined, {
  getOnInit: true,
});

/**
 * Records whether this account is looped into `roomId`.
 *
 * One setter serves both writers on purpose: the Loop in / Loop out buttons,
 * and the reconciliation from `/room/info`. They are the same operation — set
 * membership to a boolean — and giving the server-sourced write its own atom
 * would imply a distinction that does not exist.
 */
export const setLoopedInAtom = atom(
  null,
  (get, set, roomId: string, isLoopedIn: boolean) => {
    const ids = get(loopedInIdsAtom);
    const has = ids.includes(roomId);
    if (isLoopedIn === has) return;
    set(loopedInIdsAtom, isLoopedIn ? [...ids, roomId] : ids.filter((id) => id !== roomId));
  },
);

/** Reader for a single Loop's local looped-in flag. */
export const isLoopedInAtom = atom((get) => {
  const ids = get(loopedInIdsAtom);
  return (roomId: string) => ids.includes(roomId);
});

/**
 * When this device last changed a Loop's flag itself — a Loop in / Loop out tap, or
 * the loop-in a send implies. Memory only.
 *
 * The feed's rows carry the server's `isLoopedIn` and are adopted into the set, but a
 * feed response can be older than the user's own change: in flight when they tapped,
 * or served before the server applied it (a send loops the poster in only once the
 * message lands). Mobile guards the same race with a loop-in epoch.
 */
const localLoopedInAtAtom = atom<Record<string, number>>({});

/** How long a local change outranks the server's answer, from the request's start. */
export const LOOPED_IN_SETTLE_MS = 5000;

/** Records that this device just changed `roomId`'s flag (or is about to). */
export const noteLocalLoopedInAtom = atom(null, (_get, set, roomId: string) => {
  set(localLoopedInAtAtom, (m) => ({ ...m, [roomId]: Date.now() }));
});

/**
 * Adopts the server's `isLoopedIn` from feed rows fetched by a request that started
 * at `startedAt`. A row without the field, and a Loop not on the page, are left
 * alone; so is a Loop this device changed itself since shortly before the request.
 */
export const adoptServerLoopedInAtom = atom(
  null,
  (get, set, rows: readonly { id: string; isLoopedIn?: boolean }[], startedAt: number) => {
    const local = get(localLoopedInAtAtom);
    for (const row of rows) {
      if (!row.id || typeof row.isLoopedIn !== 'boolean') continue;
      const at = local[row.id];
      if (at !== undefined && at >= startedAt - LOOPED_IN_SETTLE_MS) continue;
      set(setLoopedInAtom, row.id, row.isLoopedIn);
    }
  },
);
